import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FiEdit2, FiCheck, FiX, FiBook, FiSliders, FiActivity, FiTag } from 'react-icons/fi';
import { getCapabilityScore } from '../../utils/persona_engine_utils';
import { useTheme } from '../../contexts/ThemeContext';
import { useFabricationStore } from '../../stores/fabricationStore';
import ForgeStateFlow from './ForgeStateFlow';

interface ForgeHeaderProps {
  onSave: () => void;
  onStageClick: (index: number) => void;
}

const ForgeHeader: React.FC<ForgeHeaderProps> = ({ onSave, onStageClick }) => {
  const { darkMode } = useTheme();
  const { 
    state, isDirty, isLoading, runCompile,
    auto_mode, needs_recompile, stage_completion,
    setAutoMode, currentPersonaId,
    autoSaveEnabled, autoSaveStatus, lastAutoSavedAt,
    setAutoSaveEnabled, triggerAutoSave,
  } = useFabricationStore();
  
  const [isEditing, setIsEditing] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // ── Health dashboard strip ────────────────────────────────────────────────
  const health = useMemo(() => {
    if (!state) return null;
    const wordCount = state.character_bible
      ? state.character_bible.trim().split(/\s+/).filter(Boolean).length
      : 0;
    const totalControls = state.ui_schema?.length ?? 0;
    const nonDefaultControls = state.ui_schema?.filter(
      c => c.current_value !== c.default_value
    ).length ?? 0;
    const totalProbes   = state.probes?.length ?? 0;
    const passedProbes  = state.probes?.filter(p => p.last_passed === true).length  ?? 0;
    const failedProbes  = state.probes?.filter(p => p.last_passed === false).length ?? 0;
    const untestedProbes = state.probes?.filter(p => p.last_passed === undefined).length ?? 0;
    const totalRuns     = state.probes?.reduce((acc, p) => acc + (p.run_history?.length ?? 0), 0) ?? 0;
    const capValues = Object.values(state.capabilities || {}).map(v => getCapabilityScore(v));
    const avgCap = capValues.length
      ? (capValues.reduce((a, b) => a + b, 0) / capValues.length).toFixed(1)
      : null;
    const tagCount = state.tags?.length ?? 0;
    return { wordCount, totalControls, nonDefaultControls, totalProbes, passedProbes, failedProbes, untestedProbes, totalRuns, avgCap, tagCount };
  }, [state]);
  
  // Initialize name from state
  useEffect(() => {
    if (state?.display_name) {
      setNameValue(state.display_name);
    }
  }, [state?.display_name]);
  
  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);
  
  // Start editing the name
  const startEditing = () => {
    setIsEditing(true);
  };
  
  // Save the name edit
  const saveName = () => {
    if (nameValue.trim()) {
      // Update the display_name in the store
      useFabricationStore.setState(state => ({
        state: {
          ...state.state!,
          display_name: nameValue.trim()
        },
        isDirty: true
      }));
    }
    setIsEditing(false);
  };
  
  // Cancel the name edit
  const cancelEdit = () => {
    setNameValue(state?.display_name || '');
    setIsEditing(false);
  };
  
  // Handle key presses in the input
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveName();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };
  
  
  return (
    <div className={`sticky top-0 z-10 py-3 px-4 mb-4 ${
      darkMode ? 'bg-gray-900' : 'bg-white'
    } border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
      {/* Top row: name editor */}
      <div className="flex justify-between items-center mb-3">
        <div>
        {isEditing ? (
          <div className="flex items-center">
            <input
              ref={inputRef}
              type="text"
              className={`text-xl font-bold px-2 py-1 rounded-md ${
                darkMode 
                  ? 'bg-gray-800 text-white border-gray-700' 
                  : 'bg-gray-100 text-gray-800 border-gray-300'
              } border`}
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Persona Name"
            />
            <button
              onClick={saveName}
              className={`ml-2 p-1 rounded-full ${
                darkMode ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-700'
              }`}
              aria-label="Save name"
            >
              <FiCheck size={16} />
            </button>
            <button
              onClick={cancelEdit}
              className={`ml-1 p-1 rounded-full ${
                darkMode ? 'bg-red-800 text-red-200' : 'bg-red-100 text-red-700'
              }`}
              aria-label="Cancel edit"
            >
              <FiX size={16} />
            </button>
          </div>
        ) : (
          <h1 className="text-xl font-bold flex items-center">
            {state?.display_name || 'Untitled Persona'}
            <button
              onClick={startEditing}
              className={`ml-2 p-1 rounded-full ${
                darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'
              } hover:text-blue-500 transition-colors`}
              aria-label="Edit name"
            >
              <FiEdit2 size={14} />
            </button>
          </h1>
        )}
        </div>
      </div>

      {/* Bottom row: ForgeStateFlow pipeline */}
      {state && (
        <ForgeStateFlow
          activePanel={state.active_panel_index}
          stageCompletion={stage_completion}
          needsRecompile={needs_recompile}
          probesStale={!!(
            state.probes?.length &&
            state.probes_generated_from_hash &&
            state.system_prompt_hash &&
            state.probes_generated_from_hash !== state.system_prompt_hash
          )}
          autoMode={auto_mode}
          isLoading={isLoading}
          isDirty={isDirty}
          onStageClick={onStageClick}
          onAutoModeToggle={setAutoMode}
          onRecompile={runCompile}
          onSave={onSave}
          currentPersonaId={currentPersonaId}
          autoSaveEnabled={autoSaveEnabled}
          autoSaveStatus={autoSaveStatus}
          lastAutoSavedAt={lastAutoSavedAt}
          onAutoSaveToggle={setAutoSaveEnabled}
          onAutoSaveRetry={triggerAutoSave}
          onOpenSaveDialog={onSave}
        />
      )}

      {/* Health dashboard strip */}
      {health && (
        <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 pt-2 border-t text-xs ${
          darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'
        }`}>
          <span className="flex items-center gap-1" title="Character bible word count">
            <FiBook size={11} />
            {health.wordCount.toLocaleString()} words
          </span>
          <span className="flex items-center gap-1" title={`${health.totalControls} controls, ${health.nonDefaultControls} non-default`}>
            <FiSliders size={11} />
            {health.totalControls} controls
            {health.nonDefaultControls > 0 && (
              <span className={`ml-0.5 px-1 rounded-full text-xs ${
                darkMode ? 'bg-amber-800 text-amber-300' : 'bg-amber-100 text-amber-700'
              }`}>
                {health.nonDefaultControls} active
              </span>
            )}
          </span>
          <span className="flex items-center gap-1" title={`${health.totalProbes} probes · ${health.totalRuns} total runs`}>
            🔬 {health.totalProbes} probes
            {health.passedProbes > 0 && (
              <span className={`ml-0.5 px-1 rounded-full text-xs ${
                darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-700'
              }`}>
                {health.passedProbes} ✓
              </span>
            )}
            {health.failedProbes > 0 && (
              <span className={`ml-0.5 px-1 rounded-full text-xs ${
                darkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-700'
              }`}>
                {health.failedProbes} ✗
              </span>
            )}
            {health.untestedProbes > 0 && (
              <span className={`ml-0.5 px-1 rounded-full text-xs ${
                darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
              }`}>
                {health.untestedProbes} untested
              </span>
            )}
            {health.totalRuns > 0 && (
              <span className={`ml-0.5 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                · {health.totalRuns} runs
              </span>
            )}
          </span>
          {health.avgCap && (
            <span className="flex items-center gap-1" title="Average capability score">
              ⚡ avg {health.avgCap}/10
            </span>
          )}
          {health.tagCount > 0 && (
            <span className="flex items-center gap-1" title="Tag count">
              <FiTag size={11} />
              {health.tagCount} tags
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ForgeHeader;
